'use client';

import React, { useRef } from 'react';
import Link from 'next/link';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';
import { ArrowRight } from 'lucide-react';

interface CTAButtonProps {
  label?: string;
  href?: string;
}

export const CTAButton: React.FC<CTAButtonProps> = ({
  label = 'View All Products',
  href = '/products',
}) => {
  const btnRef = useRef<HTMLAnchorElement | null>(null);
  const quickX = useRef<gsap.QuickToFunc | null>(null);
  const quickY = useRef<gsap.QuickToFunc | null>(null);

  useGSAP(() => {
    if (!btnRef.current) return;
    quickX.current = gsap.quickTo(btnRef.current, 'x', { duration: 0.4, ease: 'power3.out' });
    quickY.current = gsap.quickTo(btnRef.current, 'y', { duration: 0.4, ease: 'power3.out' });
  }, { scope: btnRef });

  const handleMouseMove = (e: React.MouseEvent<HTMLAnchorElement>) => {
    const rect = btnRef.current?.getBoundingClientRect();
    if (!rect) return;
    const relX = e.clientX - rect.left - rect.width / 2;
    const relY = e.clientY - rect.top - rect.height / 2;
    quickX.current?.(relX * 0.25);
    quickY.current?.(relY * 0.4);
  };

  const handleMouseLeave = () => {
    quickX.current?.(0);
    quickY.current?.(0);
  };

  return (
    <div className="flex justify-center mt-2">
      <Link
        ref={btnRef}
        href={href}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        className="group inline-flex items-center gap-2 px-9 py-3.5 bg-transparent border border-[#1C1B19] hover:bg-[#1C1B19] text-[#1C1B19] hover:text-[#FEFDFB] text-[12px] tracking-[0.15em] uppercase font-semibold transition-colors duration-300 will-change-transform"
      >
        {label}
        <ArrowRight
          size={15}
          strokeWidth={2}
          className="transition-transform duration-300 group-hover:translate-x-1"
        />
      </Link>
    </div>
  );
};